import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { Row, Col } from 'react-bootstrap';
import Question from '../Question';

const getOptions = (asyncUrl, input) => (
  axios.get(
    asyncUrl,
    { params: { term: input } },
  ).then((response) => {
    const compare = response.data.filter((i) => i.text.toLowerCase().includes(input.toLowerCase()));

    return compare.map((i) => ({
      label: i.text,
      value: i.id,
    }));
  })
);

const setYear = (value, yearOptions) => {
  if (value == null) return '';

  const yearIndex = yearOptions.findIndex((x) => x.value === value);
  return yearOptions[yearIndex];
};

const renderQuestion = (
  question,
  answers,
  handleInputChange,
  questionIndex,
  errors,
  disabled,
  onboardingQuestionnaire,
) => {
  const answer = answers.find((a) => a.question_key === question.question_key);
  const { value } = answer;
  const year = (new Date()).getFullYear();
  const yearOptions = Array.from(new Array(75), (val, index) => {
    const newYear = year - index;
    return { label: newYear, value: newYear };
  });
  const dropdownStyle = onboardingQuestionnaire ? {
    IndicatorSeparator: () => null,
    DropdownIndicator: () => <i className="fas fa-angle-down" />,
  } : {};

  const classNamePrefix = errors && errors.includes(question.column_name.replace('_id', ''))
    ? 'select-error' : 'react-select';

  switch (question.question_type) {
    case 'select_one':
      if (question.async_url !== null) {
        return (
          <Question
            title={question.question_text}
            className={`${question.question_type} ${question.style}`}
            subtext={question.subtext}
            onboardingQuestionnaire={onboardingQuestionnaire}
            questionKey={question.question_key}
          >
            <AsyncSelect
              components={dropdownStyle}
              className={`asyncselect ${question.question_key}`}
              loadOptions={(input) => getOptions(question.async_url, input)}
              value={value !== null ? [value] : []}
              maxMenuHeight={500}
              onChange={(option) => handleInputChange(option, answer.column_name, questionIndex)}
              isDisabled={disabled}
              onSelectResetsInput={false}
              closeOnSelect
              closeMenuOnSelect
              classNamePrefix="clora-multiselect"
              placeholder="Select or type..."
              cacheOptions
              defaultOptions
            />
          </Question>
        );
      }
      return (
        <Question
          title={question.question_text}
          className={`${question.question_type} ${question.style}`}
          subtext={question.subtext}
          onboardingQuestionnaire={onboardingQuestionnaire}
          questionKey={question.question_key}
        >
          <Select
            components={dropdownStyle}
            className={`${question.question_type} ${question.style} ${question.question_key}`}
            classNamePrefix={classNamePrefix}
            placeholder="Select or type..."
            options={question.options}
            value={value !== null ? [value] : []}
            onChange={(option) => handleInputChange(option, answer.column_name, questionIndex)}
            isDisabled={disabled}
          />
        </Question>
      );
    case 'freetext':
    case 'location':
      if (question.style === 'text_input') {
        return (
          <Question
            title={question.question_text}
            className={`${question.question_type} ${question.style}`}
            subtext={question.subtext}
            onboardingQuestionnaire={onboardingQuestionnaire}
            questionKey={question.question_key}
          >
            <div className="input-field">
              <input
                className={`input-base ${question.question_key} ${disabled ? 'disabled' : ''}`}
                type="text"
                value={value || ''}
                onChange={
                  (e) => handleInputChange(e.target.value, answer.column_name, questionIndex)
                }
                placeholder="Type here..."
                disabled={disabled}
                required
              />
            </div>
          </Question>
        );
      }
      break;
    case 'number':
      if (question.style === 'number_input') {
        return (
          <Question
            title={question.question_text}
            className={`${question.question_type} ${question.style}`}
            subtext={question.subtext}
            onboardingQuestionnaire={onboardingQuestionnaire}
            questionKey={question.question_key}
          >
            <div className="input-field">
              <input
                type="number"
                id={`${question.question_key}-number-input-${questionIndex}`}
                className={
                  `input-base number-input ${question.question_key}-number-input 
                  ${disabled ? 'disabled' : ''}`
                }
                value={value !== null ? value : ''}
                placeholder={question.question_key.indexOf('year')
                  ? new Date().getFullYear() : 'Type here...'}
                onChange={(v) => handleInputChange(v.target.value, answer.column_name, questionIndex)}
                disabled={disabled}
              />
            </div>
          </Question>
        );
      }
      break;
    case 'date':
      return (
        <Question
          title={question.question_text}
          className={`${question.question_type} ${question.style}`}
          subtext={question.subtext}
          onboardingQuestionnaire={onboardingQuestionnaire}
          questionKey={question.question_key}
        >
          <label className="g-row--always" htmlFor={`calendar-${question.question_key}`}>
            <DatePicker
              selected={value ? window.moment(value).toDate() : null}
              onChange={(val) => handleInputChange(val, answer.column_name, questionIndex)}
              allowSameDay
              className={`form-control calendar-${question.question_key}`}
              id={`calendar-${question.question_key}-${questionIndex}`}
              value={value ? window.moment(value).toDate() : window.moment().toDate()}
              dateFormat="MM/dd/yyyy"
              placeholderText="Click to select a date"
              disabled={disabled}
            />
            <i className="fa fa-calendar" />
          </label>
        </Question>
      );
    case 'year':
      return (
        <Question
          title={question.question_text}
          className={`${question.question_type} ${question.style}`}
          subtext={question.subtext}
          onboardingQuestionnaire={onboardingQuestionnaire}
          questionKey={question.question_key}
        >
          <Select
            components={dropdownStyle}
            className={`${question.question_type} ${question.style} ${question.question_key}`}
            classNamePrefix={classNamePrefix}
            placeholder="Select"
            options={yearOptions}
            value={setYear(value, yearOptions)}
            onChange={(option) => handleInputChange(option, answer.column_name, questionIndex)}
            isDisabled={disabled}
          />
        </Question>
      );
    default:
      return null;
  }
  return null;
};

const renderErrorMessage = (errors) => {
  const { length } = errors;
  return (
    <div className="error-message">
      <i className="fa fa-exclamation-triangle" />
      {errors.length > 0 && `Please enter ${errors.slice(0, length - 1).join(', ')} ${length > 1 ? 'and' : ''} ${errors.slice(-1)}. `}
    </div>
  );
};

function Form({
  questions,
  answers,
  index,
  handleInputChange,
  handleDelete,
  errors,
  disabled,
  onboardingQuestionnaire,
  questionKey,
  formCount,
}) {
  const className = onboardingQuestionnaire ? `onboarding-${questionKey}` : '';
  return (
    <Row className={`group-parent-form ${className}`}>
      <Col xs={onboardingQuestionnaire ? 12 : 11}>
        <Row>
          {errors.length > 0 && renderErrorMessage(errors)}
          {questions.map((question) => (
            <Col xs={['number', 'year'].includes(question.question_type) ? 6 : 12} className={`${question.question_type}`}>
              { renderQuestion(
                question,
                answers,
                handleInputChange,
                index,
                errors,
                disabled,
                onboardingQuestionnaire,
              ) }
            </Col>
          ))}
        </Row>
      </Col>
      <Col xs={onboardingQuestionnaire ? 12 : 1} className={`${onboardingQuestionnaire ? 'text-right' : ''}`}>
        {
          formCount > 0
            && (
            <div className="delete-grouping">
              <a
                role="button"
                tabIndex={0}
                type="button"
                id="delete-grouping"
                className={disabled ? 'disabled' : ''}
                onClick={disabled ? null : () => handleDelete(index)}
              >
                <i className="fa fa-trash-alt" />
              </a>
            </div>
            )
        }
      </Col>
    </Row>
  );
}

Form.propTypes = {
  questions: PropTypes.arrayOf(PropTypes.shape({})),
  answers: PropTypes.arrayOf(PropTypes.shape({})),
  index: PropTypes.number.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  errors: PropTypes.arrayOf(PropTypes.shape({})),
  disabled: PropTypes.bool,
  onboardingQuestionnaire: PropTypes.bool,
  questionKey: PropTypes.string.isRequired,
  formCount: PropTypes.number,
};

Form.defaultProps = {
  disabled: false,
  onboardingQuestionnaire: false,
  formCount: 0,
};

export default Form;
